/**
 * Direct RunPod integration - no backend server needed
 * Calls RunPod API directly from the browser
 */

interface RunPodConfig {
  apiKey: string
  dockerImage?: string
  maxConcurrentPods?: number
}

interface ExecutionConfig {
  features: Record<string, any>
  execution: Record<string, any>
  advanced: Record<string, any>
}

class RunPodDirect {
  private apiKey: string
  private dockerImage: string
  private activePod: string | null = null
  private graphqlUrl = 'https://api.runpod.io/graphql'
  
  constructor(config: RunPodConfig) {
    // Get API key from localStorage or config
    this.apiKey = config.apiKey || localStorage.getItem('RUNPOD_API_KEY') || ''
    this.dockerImage = config.dockerImage || 'runpod/base:0.0.0'
    
    if (!this.apiKey) {
      throw new Error('RunPod API key not configured. Please set it in settings.')
    }
  }
  
  /**
   * Create and start a pod for execution
   */
  async createPod(config: ExecutionConfig | {env_vars: Record<string, string>}): Promise<string> {
    // Only allow one pod at a time
    if (this.activePod) {
      throw new Error('A pod is already running. Please wait for it to complete.')
    }
    
    const mutation = `
      mutation CreatePod($input: PodInput!) {
        createPod(input: $input) {
          id
          name
          desiredStatus {
            podHostname
          }
          actualStatus {
            state
          }
        }
      }
    `
    
    // Build environment variables
    const env: Record<string, string> = {}
    
    // Check if new format with env_vars directly
    if ('env_vars' in config) {
      // New format: env_vars passed directly from configToEnvVars
      for (const [key, value] of Object.entries(config.env_vars)) {
        if (value !== undefined && value !== null && value !== '') {
          env[key] = String(value)
        }
      }
    } else {
      // Legacy format: process all config groups and use keys as-is
      const execConfig = config as ExecutionConfig
      for (const [key, value] of Object.entries(execConfig.features)) {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            env[key] = value.join(',')
          } else if (typeof value === 'boolean') {
            env[key] = value ? '1' : '0'
          } else {
            env[key] = String(value)
          }
        }
      }
      
      for (const [key, value] of Object.entries(execConfig.execution)) {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            env[key] = value.join(',')
          } else if (typeof value === 'boolean') {
            env[key] = value ? '1' : '0'
          } else {
            env[key] = String(value)
          }
        }
      }
      
      for (const [key, value] of Object.entries(execConfig.advanced || {})) {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            env[key] = value.join(',')
          } else if (typeof value === 'boolean') {
            env[key] = value ? '1' : '0'
          } else {
            env[key] = String(value)
          }
        }
      }
    }
    
    // Ensure DIR_NAME exists
    if (!env.DIR_NAME) {
      env.DIR_NAME = `runpod-${Date.now()}`
    }
    
    // Build command
    env.EXECUTION_COMMAND = 'cd /workspace/Eval-Reasoning-Consistency && python -m scripts.run_reasoning && python -m scripts.run_analyzers'
    
    const podInput = {
      name: `erc-${Date.now()}`,
      imageUri: this.dockerImage,
      gpuTypeId: null, // No GPU
      cloudType: 'COMMUNITY',
      region: 'US',
      supportPublicIp: false,
      startSsh: false,
      gpuCount: 0,
      cpuCount: 4,
      memoryInGb: 8,
      volumeInGb: 20,
      volumeMountPath: '/workspace',
      env,
      stopAfterExecution: true,
      ttlSeconds: 3600 // 1 hour max
    }
    
    const response = await fetch(this.graphqlUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        query: mutation,
        variables: { input: podInput }
      })
    })
    
    const result = await response.json()
    
    if (result.errors) {
      throw new Error(`RunPod error: ${result.errors[0].message}`)
    }
    
    this.activePod = result.data.createPod.id
    return this.activePod as string
  }
  
  /**
   * Get pod status
   */
  async getPodStatus(podId: string): Promise<string> {
    const query = `
      query GetPod($podId: String!) {
        pod(podId: $podId) {
          actualStatus {
            state
          }
        }
      }
    `
    
    const response = await fetch(this.graphqlUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        query,
        variables: { podId }
      })
    })
    
    const result = await response.json()
    return result.data?.pod?.actualStatus?.state || 'UNKNOWN'
  }
  
  /**
   * Get pod logs
   */
  async getPodLogs(podId: string, lines: number = 100): Promise<string> {
    const response = await fetch(`https://api.runpod.io/v2/pods/${podId}/logs?lines=${lines}`, {
      headers: {
        'Authorization': `Bearer ${this.apiKey}`
      }
    })
    
    if (response.ok) {
      return await response.text()
    }
    return ''
  }
  
  /**
   * Terminate pod
   */
  async terminatePod(podId: string): Promise<boolean> {
    const mutation = `
      mutation TerminatePod($podId: String!) {
        terminatePod(podId: $podId) {
          success
        }
      }
    `
    
    const response = await fetch(this.graphqlUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        query: mutation,
        variables: { podId }
      })
    })
    
    const result = await response.json()
    
    if (podId === this.activePod) {
      this.activePod = null
    }
    
    return result.data?.terminatePod?.success || false
  }
  
  /**
   * Stream logs with polling
   */
  async* streamLogs(podId: string): AsyncGenerator<string> {
    let lastLogLength = 0
    let noUpdateCount = 0
    const maxNoUpdates = 30 // 1 minute of no updates
    
    while (true) {
      try {
        // Get current logs
        const logs = await this.getPodLogs(podId, 500)
        
        // Check for new content
        if (logs.length > lastLogLength) {
          const newLogs = logs.substring(lastLogLength)
          lastLogLength = logs.length
          noUpdateCount = 0
          yield newLogs
          
          // Check for completion
          if (logs.includes('Execution complete') || logs.includes('ERROR')) {
            break
          }
        } else {
          noUpdateCount++
          if (noUpdateCount > maxNoUpdates) {
            yield '\n[Timeout: No output for 1 minute]\n'
            break
          }
        }
        
        // Check pod status
        const status = await this.getPodStatus(podId)
        if (status === 'TERMINATED' || status === 'FAILED') {
          yield `\n[Pod ${status.toLowerCase()}]\n`
          break
        }
        
        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, 2000))
        
      } catch (error) {
        yield `\n[Error: ${error}]\n`
        break
      }
    }
    
    // Cleanup
    if (podId === this.activePod) {
      this.activePod = null
    }
  }
}

// Export for use in React components
export default RunPodDirect
export type { RunPodConfig, ExecutionConfig }